import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { type NewOrder } from '@pkg/graphql/codegen/graphql';
import { type PrepareOrderType } from '@pkg/hooks/marketplace/usePrepareOrder';
import { type MarketplaceAsset, type MarketplaceCollectible } from '@pkg/types';

type Asset = MarketplaceAsset<MarketplaceCollectible['collection']>;
interface State {
    confirmedToken: Asset | null;
    token: Asset | null;
    orderRequestPendingId?: string;
    orderType?: PrepareOrderType;
    pendingConfirmation: boolean;
    listingOrderRequest?: Partial<NewOrder>;
}

interface MarketplaceOrdersStore extends State {
    addConfirmedToken: (token: Asset) => void;
    addToken: (token: Asset, orderType: PrepareOrderType) => void;
    removeConfirmedToken: () => void;
    removeToken: () => void;
    setPendingConfirmation: (pending?: boolean) => void;
    setOrderRequestPendingId: (orderRequestPendingId?: string) => void;
    setListingOrderRequest: (request: State['listingOrderRequest']) => void;
    reset: () => void;
}

const initialState: State = {
    orderType: undefined,
    confirmedToken: null,
    listingOrderRequest: undefined,
    pendingConfirmation: false,
    token: null,
    orderRequestPendingId: undefined,
};

export const useOrdersStore = create<MarketplaceOrdersStore>()(
    devtools(
        (set) => ({
            ...initialState,
            addToken: (token, orderType) => set({ token, orderType }),
            setListingOrderRequest: (listingOrderRequest) =>
                set((state) => ({ listingOrderRequest, pendingConfirmation: state?.orderType === 'buy' })),
            setOrderRequestPendingId: (orderRequestPendingId) => set({ orderRequestPendingId }),
            addConfirmedToken: (confirmedToken) =>
                set({
                    confirmedToken,
                    token: null,
                    pendingConfirmation: false,
                    listingOrderRequest: undefined,
                }),
            removeConfirmedToken: () =>
                set({ confirmedToken: null, orderType: undefined, listingOrderRequest: undefined }),
            removeToken: () => set({ token: null, listingOrderRequest: undefined }),
            setPendingConfirmation: (pendingConfirmation = false) => set({ pendingConfirmation }),
            reset: () => set(initialState),
        }),
        { name: 'order-store' },
    ),
);
